import React from 'react';
import { Icon, Spin, StatusPill, Tabs, FG, Pagination } from '../components/ui.jsx';
import { api, fmt, fmtD } from '../api.js';
import { ChartCanvas } from '../components/chart.jsx';
import { MONTHS, PAGE_SIZE } from '../constants.js';

export const WorkloadPage = () => {
  const now = new Date();
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState(null);
  const [filter, setFilter] = React.useState('');
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const load = () => {
    setLoading(true);
    let qs = `?year=${year}`;
    if (month) qs += `&month=${month}`;
    if (filter) qs += `&employment_type=${filter}`;
    api(`/workload${qs}`).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  };
  React.useEffect(() => { load(); }, [year, month, filter]);

  const techs = data?.technicians || [];
  const summary = data?.summary || {};

  const satColor = (pct) => {
    if (pct >= 80) return {bg:'#FEE2E2',color:'#991B1B',label:'Sovraccarico'};
    if (pct >= 50) return {bg:'#FEF3C7',color:'#92400E',label:'Alto'};
    if (pct >= 20) return {bg:'#D1FAE5',color:'#065F46',label:'Buono'};
    return {bg:'#F3F4F6',color:'#6B7280',label:'Basso'};
  };

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 2; y--) years.push(y);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Carichi di lavoro</div>
          <div className="page-title-sub">{techs.length} tecnici attivi nel {year}</div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <select className="input" style={{width:130}} value={filter}
            onChange={e => setFilter(e.target.value)}>
            <option value="">Tutti</option>
            <option value="dipendente">Interni</option>
            <option value="freelance">Esterni</option>
          </select>
          <select className="input" style={{width:130}} value={month||''}
            onChange={e => setMonth(e.target.value ? +e.target.value : null)}>
            <option value="">Tutto l'anno</option>
            {MONTHS.slice(1).map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <a href={`/api/export/workload?year=${year}`} download className="btn btn-ghost btn-sm" style={{fontSize:11}}>
            <Icon name="download" size={12} color="var(--n500)"/>CSV
          </a>
          <select className="input" style={{width:90}} value={year}
            onChange={e => setYear(+e.target.value)}>
            {years.map(y => <option key={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {loading && <div className="empty"><Spin/> Caricamento...</div>}
      {!loading && data && (
        <>
          {/* KPI */}
          <div className="kpi-row" style={{marginBottom:16}}>
            {[
              {icon:'users',val:summary.total_techs||0,label:'Tecnici',color:'var(--n800)'},
              {icon:'calendar',val:summary.total_giorni||0,label:'Giornate totali',color:'var(--blue)'},
              {icon:'trending-up',val:fmt(summary.total_compenso||0),label:'Compensi totali',color:'#065F46'},
              {icon:'activity',val:`${summary.avg_saturation||0}%`,label:'Saturazione media',
                color: (summary.avg_saturation||0)>=50?'#92400E':'#065F46'},
            ].map(k => (
              <div key={k.label} className="kpi-card">
                <Icon name={k.icon} size={18} color={k.color} style={{marginBottom:8}}/>
                <div className="kpi-val" style={{color:k.color,fontSize:'1.3rem'}}>{k.val}</div>
                <div className="kpi-lbl">{k.label}</div>
              </div>
            ))}
          </div>

          {/* Interni vs Esterni */}
          <div className="grid grid-2" style={{marginBottom:16}}>
            <div className="card" style={{borderLeft:'3px solid var(--blue)'}}>
              <div className="card-title"><Icon name="building" size={12} color="var(--blue)"/>Interni (dipendenti)</div>
              <div style={{display:'flex',gap:20}}>
                <div><div style={{fontSize:22,fontWeight:700,color:'var(--blue)'}}>{summary.interni||0}</div><div style={{fontSize:11,color:'var(--n400)'}}>tecnici</div></div>
                <div><div style={{fontSize:22,fontWeight:700,color:'var(--n700)'}}>{summary.giorni_interni||0}</div><div style={{fontSize:11,color:'var(--n400)'}}>giornate</div></div>
                <div><div style={{fontSize:22,fontWeight:700,color:'#065F46'}}>{fmt(summary.costo_interni||0)}</div><div style={{fontSize:11,color:'var(--n400)'}}>costo</div></div>
              </div>
            </div>
            <div className="card" style={{borderLeft:'3px solid #F59E0B'}}>
              <div className="card-title"><Icon name="user" size={12} color="#F59E0B"/>Esterni (freelance)</div>
              <div style={{display:'flex',gap:20}}>
                <div><div style={{fontSize:22,fontWeight:700,color:'#92400E'}}>{summary.esterni||0}</div><div style={{fontSize:11,color:'var(--n400)'}}>tecnici</div></div>
                <div><div style={{fontSize:22,fontWeight:700,color:'var(--n700)'}}>{summary.giorni_esterni||0}</div><div style={{fontSize:11,color:'var(--n400)'}}>giornate</div></div>
                <div><div style={{fontSize:22,fontWeight:700,color:'#065F46'}}>{fmt(summary.costo_esterni||0)}</div><div style={{fontSize:11,color:'var(--n400)'}}>costo</div></div>
              </div>
            </div>
          </div>

          {/* Grafico saturazione */}
          <div className="card" style={{marginBottom:16}}>
            <div className="card-title"><Icon name="activity" size={13} color="var(--blue)"/> Saturazione per tecnico</div>
            <ChartCanvas type="bar" height={380} data={{
              labels: techs.slice(0,12).map(t => t.name),
              datasets: [{
                label:'Saturazione %', data: techs.slice(0,12).map(t => +(t.saturation_pct||0)),
                backgroundColor: techs.slice(0,12).map(t => {
                  const p = +(t.saturation_pct||0);
                  return p>=80?'rgba(239,68,68,0.7)':p>=50?'rgba(245,158,11,0.7)':p>=20?'rgba(14,159,110,0.6)':'rgba(148,163,184,0.4)';
                }),
                borderRadius: 6, barPercentage: 0.65,
              }]
            }} options={{
              indexAxis:'y',
              scales:{x:{beginAtZero:true,max:100,ticks:{font:{size:10},callback:v=>v+'%'},grid:{color:'rgba(0,0,0,.04)'}},
                      y:{ticks:{font:{size:11}},grid:{display:false}}},
              plugins:{legend:{display:false}}
            }}/>
          </div>

          {/* Tabella tecnici */}
          <div className="card" style={{padding:0}}>
            <table>
              <thead>
                <tr>
                  <th>Tecnico</th><th>Tipo</th><th style={{textAlign:'right'}}>Giornate</th>
                  <th style={{textAlign:'right'}}>Cantieri</th><th style={{textAlign:'right'}}>Compenso</th>
                  <th>Saturazione</th><th>Dettaglio mesi</th>
                </tr>
              </thead>
              <tbody>
                {techs.map(t => {
                  const sc = satColor(t.saturation_pct||0);
                  return (
                    <tr key={t.technician_id}>
                      <td style={{fontWeight:600,fontSize:13}}>{t.name}</td>
                      <td><span className={`emp-badge emp-${t.employment_type||'freelance'}`}>{t.employment_type}</span></td>
                      <td style={{textAlign:'right',fontWeight:700,fontSize:14}}>{t.total_giorni}</td>
                      <td style={{textAlign:'right',color:'var(--n400)',fontSize:13}}>{t.total_cantieri}</td>
                      <td style={{textAlign:'right',fontWeight:600,color:'#B87D00',fontSize:12}}>{fmt(t.total_compenso)}</td>
                      <td>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <div style={{flex:1,height:6,background:'var(--n100)',borderRadius:3,maxWidth:80}}>
                            <div style={{width:`${Math.min(t.saturation_pct||0,100)}%`,height:'100%',
                              background:sc.color,borderRadius:3,transition:'width .3s'}}/>
                          </div>
                          <span style={{fontSize:11,fontWeight:600,color:sc.color}}>{t.saturation_pct||0}%</span>
                        </div>
                      </td>
                      <td style={{fontSize:11,color:'var(--n400)'}}>
                        {(t.months||[]).map(m => (
                          <span key={m.month} style={{display:'inline-block',marginRight:8}}>
                            <span style={{fontWeight:600,color:'var(--n600)'}}>{MONTHS[m.month]?.substring(0,3)}</span>
                            {' '}{m.giorni}g / {m.cantieri}c
                          </span>
                        ))}
                      </td>
                    </tr>
                  );
                })}
                {!techs.length && <tr><td colSpan={7} className="empty" style={{padding:24}}>Nessun dato di lavoro</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
